import { useEffect, useMemo, useState } from "react";
import JobZImage from "../../../common/jobz-img";
import { adminService } from "../../../../services/admin-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge, formatDate } from "./admin-ui";

const emptyForm = { id: "", owner_user_id: "", name: "", website: "", location: "", description: "", verified: false };

function AdminCompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [message, setMessage] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [bannerFile, setBannerFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const selectedCompany = useMemo(() => companies.find((company) => company.id === form.id), [companies, form.id]);

    const load = () => Promise.all([adminService.companies(), adminService.users({ role: "employer" })])
        .then(([companyData, userData]) => {
            setCompanies(companyData.companies || []);
            setUsers(userData.users || []);
        })
        .catch((error) => setMessage(error.message));

    useEffect(() => {
        load();
    }, []);

    const verify = async (company, verified) => {
        try {
            await adminService.verifyCompany(company.id, verified);
            setMessage(`${company.name} verification updated.`);
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const selectCompany = (company) => {
        setForm({
            id: company.id || "",
            owner_user_id: company.owner_user_id || "",
            name: company.name || "",
            website: company.website || "",
            location: company.location || "",
            description: company.description || "",
            verified: Boolean(company.verified)
        });
        setLogoFile(null);
        setBannerFile(null);
    };

    const addNew = () => {
        setForm(emptyForm);
        setLogoFile(null);
        setBannerFile(null);
        setMessage("Add a new company and assign it to an employer.");
    };

    const update = (event) => {
        const { name, type, checked, value } = event.target;
        setForm({ ...form, [name]: type === "checkbox" ? checked : value });
    };

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setMessage("");
        try {
            const payload = { ...form };
            const data = form.id ? await adminService.updateCompany(form.id, payload) : await adminService.createCompany(payload);
            const company = data.company;
            if (company?.id && logoFile) await adminService.uploadCompanyLogo(company.id, logoFile);
            if (company?.id && bannerFile) await adminService.uploadCompanyBanner(company.id, bannerFile);
            await load();
            if (company?.id) selectCompany({ ...company, owner_user_id: payload.owner_user_id || company.owner_user_id });
            setMessage("Company details saved.");
        } catch (error) {
            setMessage(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AdminPageHeader title="Companies" subtitle="Employer verification and monitoring" />
            {message && <div className="alert alert-info">{message}</div>}
            <AdminPanel title={form.id ? "Edit Company" : "Add Company"} icon="fa fa-edit">
                {selectedCompany && (
                    <div className="row m-b20">
                        <div className="col-md-2"><JobZImage src={selectedCompany.logo_url || "images/jobs-company/pic1.jpg"} alt={selectedCompany.name} /></div>
                        <div className="col-md-10">
                            <strong>{selectedCompany.name}</strong>
                            <div className="text-muted">{selectedCompany.website}</div>
                            {selectedCompany.banner_url && <div className="m-t10"><JobZImage src={selectedCompany.banner_url} alt={`${selectedCompany.name} banner`} /></div>}
                        </div>
                    </div>
                )}
                <form onSubmit={submit}>
                    <div className="row">
                        <div className="col-xl-4 col-lg-6 col-md-12"><div className="form-group"><label>Employer Owner</label><select className="form-control" name="owner_user_id" required={!form.id} value={form.owner_user_id} onChange={update}><option value="">Select employer</option>{users.map((user) => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}</select></div></div>
                        <Field label="Company Name" name="name" value={form.name} onChange={update} required />
                        <Field label="Website" name="website" value={form.website} onChange={update} placeholder="https://..." />
                        <Field label="Location" name="location" value={form.location} onChange={update} />
                        <div className="col-xl-6 col-lg-6 col-md-12"><div className="form-group"><label>Logo</label><input className="form-control" type="file" accept="image/*" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} /></div></div>
                        <div className="col-xl-6 col-lg-6 col-md-12"><div className="form-group"><label>Banner</label><input className="form-control" type="file" accept="image/*" onChange={(event) => setBannerFile(event.target.files?.[0] || null)} /></div></div>
                        <div className="col-md-12"><div className="form-group"><label>Description</label><textarea className="form-control" name="description" rows={4} value={form.description} onChange={update} /></div></div>
                        <div className="col-md-12"><label><input type="checkbox" name="verified" checked={form.verified} onChange={update} /> Approve company now</label></div>
                        <div className="col-md-12 m-t20"><button className="site-button" type="submit" disabled={submitting}>{submitting ? "Saving..." : "Save Company"}</button><button className="site-button-secondry m-l10" type="button" onClick={addNew}>Clear / Add New</button></div>
                    </div>
                </form>
            </AdminPanel>
            <AdminPanel title="Company Verification" icon="fa fa-building">
                <div className="table-responsive">
                    <table className="table table-bordered">
                        <thead><tr><th>Company</th><th>Employer</th><th>Location</th><th>Jobs</th><th>Status</th><th>Created</th><th>Action</th></tr></thead>
                        <tbody>
                            {companies.length === 0 ? (
                                <tr><td colSpan="7"><EmptyState text="No companies found." /></td></tr>
                            ) : companies.map((company, index) => (
                                <tr key={company.id}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={{ width: 48, height: 48 }}>
                                                <JobZImage src={company.logo_url || `images/jobs-company/pic${(index % 5) + 1}.jpg`} alt={company.name} />
                                            </div>
                                            <div><strong>{company.name}</strong><div className="text-muted">{company.website}</div></div>
                                        </div>
                                    </td>
                                    <td>{company.owner_name || ""}<div className="text-muted">{company.owner_email || ""}</div></td>
                                    <td>{company.location || ""}</td>
                                    <td>{company.job_count || 0}</td>
                                    <td><StatusBadge status={company.verified ? "verified" : "unverified"} /></td>
                                    <td>{formatDate(company.created_at)}</td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-success me-1" type="button" onClick={() => verify(company, true)}>Verify</button>
                                        <button className="btn btn-sm btn-outline-danger me-1" type="button" onClick={() => verify(company, false)}>Unverify</button>
                                        <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => selectCompany(company)}>Edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </AdminPanel>
        </>
    );
}

function Field({ label, name, value, onChange, required = false, placeholder = "" }) {
    return <div className="col-xl-4 col-lg-6 col-md-12"><div className="form-group"><label>{label}</label><input className="form-control" name={name} type="text" required={required} placeholder={placeholder || label} value={value} onChange={onChange} /></div></div>;
}

export default AdminCompaniesPage;
